import React, { useMemo, useState } from 'react';
import PropTypes from 'prop-types';
import {
  Icon, Button, Header, Accordion, Message,
} from 'semantic-ui-react';
import { isEmpty } from 'lodash';
import './EligibilityResultsLog.css';

// One rule outcome, rendered as a tinted card. The reason is the
// operator-facing explanation from the backend; passing rules usually come back
// without one.
function RuleCard({ rule }) {
  const passed = rule.outcome !== 'fail';
  const reason = rule.reason || (passed ? `${rule.ruleName} check passed` : null);

  return (
    <Message
      icon
      positive={passed}
      negative={!passed}
      className='eligibility-rule'
      data-testid={`force-guarantee-rule-${rule.id}`}
    >
      <Icon name={passed ? 'check' : 'cancel'} />
      <Message.Content>
        <Message.Header>{rule.ruleName}</Message.Header>
        {reason && <p>{reason}</p>}
      </Message.Content>
    </Message>
  );
}

RuleCard.propTypes = {
  rule: PropTypes.object.isRequired,
};

function RuleList({ rules }) {
  return (
    <div className='eligibility-rule-list'>
      {rules.map(rule => (
        <RuleCard key={rule.id} rule={rule} />
      ))}
    </div>
  );
}

RuleList.propTypes = {
  rules: PropTypes.arrayOf(PropTypes.object).isRequired,
};

// Full eligibility log. Failed rules are listed first, passed rules are grouped
// into a collapsible section so the failures stay above the fold.
function EligibilityResultsLog({
  results, vin, productName, disabled, createDisabled, onCreateGuarantee, onBack,
}) {
  const [passedExpanded, setPassedExpanded] = useState(false);

  const { failedRules, passedRules } = useMemo(
    () => ({
      failedRules: results.filter(r => r.outcome === 'fail'),
      passedRules: results.filter(r => r.outcome !== 'fail'),
    }),
    [results]
  );

  if (isEmpty(results)) return null;

  const failedCount = failedRules.length;
  const hasFailures = failedCount > 0;

  return (
    <div data-testid='force-guarantee-eligibility-log'>
      <Header as='h3'>
        {vin ? `Eligibility Results for VIN: ${vin}` : 'Eligibility Results'}
        {productName && (
          <Header.Subheader>
            {`Checking ${results.length} rules for ${productName}`}
          </Header.Subheader>
        )}
      </Header>

      {hasFailures ? (
        <div data-testid='force-guarantee-failed-rules'>
          <Header as='h5' color='red' className='eligibility-rules-heading'>
            <Icon name='cancel' color='red' />
            {`Failed Rules (${failedCount})`}
          </Header>
          <RuleList rules={failedRules} />
        </div>
      ) : (
        <Message
          positive
          icon='check'
          header='All eligibility rules passed'
          content={`All ${results.length} rules passed for this transaction.`}
          className='eligibility-rule'
        />
      )}

      {!isEmpty(passedRules) && (
        <Accordion data-testid='force-guarantee-passed-rules'>
          <Accordion.Title
            active={passedExpanded}
            onClick={() => setPassedExpanded(!passedExpanded)}
            className='eligibility-passed-toggle'
            data-testid='force-guarantee-passed-rules-toggle'
          >
            <Icon name='dropdown' />
            <Header as='h5' color='green' className='eligibility-rules-heading'>
              <Icon name='check' color='green' />
              {`Passed Rules (${passedRules.length})`}
            </Header>
          </Accordion.Title>
          <Accordion.Content active={passedExpanded}>
            <RuleList rules={passedRules} />
          </Accordion.Content>
        </Accordion>
      )}

      <div className='eligibility-actions'>
        {onBack && (
          <Button
            content='Back'
            onClick={onBack}
            disabled={disabled}
            data-testid='force-guarantee-back-button'
          />
        )}
        <Button
          content={hasFailures ? 'Force Guarantee Anyway' : 'Create Guarantee'}
          color={hasFailures ? 'red' : 'green'}
          disabled={disabled || createDisabled}
          onClick={onCreateGuarantee}
          data-testid='force-guarantee-create-button'
        />
      </div>
    </div>
  );
}

EligibilityResultsLog.propTypes = {
  results: PropTypes.arrayOf(
    PropTypes.shape({
      id: PropTypes.string,
      ruleName: PropTypes.string,
      outcome: PropTypes.oneOf(['pass', 'fail']),
      reason: PropTypes.string,
    })
  ),
  vin: PropTypes.string,
  productName: PropTypes.string,
  disabled: PropTypes.bool,
  createDisabled: PropTypes.bool,
  onCreateGuarantee: PropTypes.func.isRequired,
  onBack: PropTypes.func,
};

EligibilityResultsLog.defaultProps = {
  results: [],
  vin: '',
  productName: '',
  disabled: false,
  createDisabled: false,
  onBack: null,
};

export default EligibilityResultsLog;
